<ng-container *ngLet="(screenSizeService.isHandset$ | async) as isHandset">
  <button
    *ngIf="(allowCreateDpr$ | async) && selectedSubproject"
    mat-flat-button
    class="floatingAddButton"
    color="primary"
    data-testid="add-new-dpr"
    appMorphingButton
    [isEnabled]="!isHandset"
    [scrollableSelector]="'.main-container'"
    (click)="onCreateDpr()"
  >
    <mat-icon svgIcon="add"></mat-icon>
    <span *ngIf="!isHandset" class="label" i18n="@@dpr.dprsPage.btnNewDpr">New DPR</span>
  </button>

  <div *ngLet="dprCardsCount$| async as dprCardsCount" class="position-relative">
    <div class="pageHeader" appStickyPageHeader>
      <h2 class="pageTitle" data-testid="dprs-page-title" i18n="@@dpr.dprsPage.titleDPRs">DPRs</h2>

      <div *ngIf="!isHandset" class="buttonsWrapper">
        <div
          [matTooltip]="(isAppOnline$ | async) | downloadButtonDisabledTooltip : selectedSubproject.canDownloadDprFiles : !!dprCardsCount"
        >
          <button
            mat-flat-button
            data-testid="download-dpr"
            color="secondary"
            (click)="onDownloadDprFiles()"
            [disabled]="!selectedSubproject.canDownloadDprFiles || (isAppOnline$ | async) === false || !dprCardsCount"
          >
            <mat-icon svgIcon="cloud-download"></mat-icon>
            <span i18n="@@dpr.dprsPage.btnDownload">Download</span>
          </button>
        </div>

        <div
          [matTooltip]="(isAppOnline$ | async) | downloadButtonDisabledTooltip : selectedSubproject.canExportData : !!dprCardsCount"
        >
          <button
            class="ml-2"
            mat-flat-button
            data-testid="download-excel"
            color="secondary"
            (click)="onDownloadExcel()"
            [disabled]="!selectedSubproject.canExportData || (isAppOnline$ | async) === false || !dprCardsCount"
          >
            <mat-icon svgIcon="file-excel"></mat-icon>
            <span i18n="@@dpr.dprsPage.btnExcel">Excel</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Sort -->
    <div class="pageHeader sortHeader">
      <div class="d-flex align-items-center">
        <div *ngIf="dprCardsCount !== null" class="entriesCount">
          <span data-testid="number-of-entries" [ngPlural]="dprCardsCount ?? 0">
            {{dprCardsCount}}
            <ng-template ngPluralCase="=1">{{ entityName}}</ng-template>
            <ng-template ngPluralCase="other">{{ entityNamePlural}}</ng-template>
          </span>
        </div>

        <!-- Sort -->
        <ng-container *ngIf="!isHandset">
          <button
            mat-button
            color="primary"
            data-testid="sort-options"
            [matMenuTriggerFor]="sortOptions"
            [disabled]="(isAppOnline$ | async) === false"
          >
            <mat-icon
              [svgIcon]="filters.sortingDirection === SortDirection.Ascending ? 'sort-ascending' : 'sort-descending'"
            ></mat-icon>
            <span i18n="@@dpr.dprPage.btnSortBy">
              Sort by {{sortingOptions | getMapValue : filters.sortingDirection}}
            </span>
            <mat-icon class="iconRight" svgIcon="menu-down"></mat-icon>
          </button>
          <mat-menu #sortOptions="matMenu">
            <button
              data-testid="sort-by-oldest-to-newest"
              mat-menu-item
              (click)="onSortColumnChanged(SortDirection.Ascending)"
            >
              {{sortByOldestToNewestTitle}}
            </button>
            <button
              data-testid="sort-by-newest-to-oldest"
              mat-menu-item
              (click)="onSortColumnChanged(SortDirection.Descending)"
            >
              {{sortByNewestToOldestTitle}}
            </button>
          </mat-menu>

          <!-- DPR Filter -->
          <button
            mat-button
            (click)="isStatusFiltersOpen = !isStatusFiltersOpen"
            color="primary"
            data-testid="status-filter"
            cdkOverlayOrigin
            #statusFilterBtn="cdkOverlayOrigin"
            [disabled]="(isAppOnline$ | async) === false"
          >
            <ng-container *ngLet="filters | selectedStatusFilterNames : statusFilterNames as selectedStatusFilters">
              <mat-icon svgIcon="filter"></mat-icon>
              <span
                data-testid="all-dprs-status-filter"
                *ngIf="!selectedStatusFilters.length"
                i18n="@@dpr.dprPage.btnStatusFilterAllDprs"
              >
                All DPRs
              </span>
              <ng-container *ngLet="selectedStatusFilters | arrayMore as selectedStatusFiltersDisplay">
                <span data-testid="selected-status-filter">{{selectedStatusFiltersDisplay.display}}</span>
                <span class="ml-2" *ngIf="selectedStatusFiltersDisplay.more">
                  {{StaticMessage.ArrayMoreText | staticMessage : selectedStatusFiltersDisplay.more }}
                </span>
              </ng-container>
              <mat-icon class="iconRight" svgIcon="menu-down"></mat-icon>
            </ng-container>
          </button>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="statusFilterBtn"
            [cdkConnectedOverlayOpen]="isStatusFiltersOpen"
            cdkConnectedOverlayHasBackdrop="true"
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            (backdropClick)="isStatusFiltersOpen=false"
          >
            <app-dpr-status-filter
              [isCompleted]="filters.isCompleted"
              [isIncompleted]="filters.isIncompleted"
              [isLocked]="filters.isLocked"
              [isUnlocked]="filters.isUnlocked"
              [isSignedDprDetailsUploaded]="filters.isSignedDprDetailsUploaded"
              [isSignedDprDetailsNotUploaded]="filters.isSignedDprDetailsNotUploaded"
              [names]="statusFilterNames"
              (cancel)="isStatusFiltersOpen=false"
              (selectionChanged)="onStatusFilterChanged($event)"
            ></app-dpr-status-filter>
          </ng-template>

          <!--Time filter-->
          <button
            mat-button
            (click)="isDateRangeFilterOpen = !isDateRangeFilterOpen"
            color="primary"
            cdkOverlayOrigin
            data-testid="time-filter"
            #dateRangeFilterBtn="cdkOverlayOrigin"
            [disabled]="(isAppOnline$ | async) === false"
          >
            <mat-icon svgIcon="filter"></mat-icon>
            <span>{{filters.startDate | dateRangeDuration : filters.endDate}}</span>
            <mat-icon class="iconRight" svgIcon="menu-down"></mat-icon>
          </button>
          <ng-template
            cdkConnectedOverlay
            cdkConnectedOverlayPanelClass="dprDateRangePicker"
            [cdkConnectedOverlayOrigin]="dateRangeFilterBtn"
            [cdkConnectedOverlayOpen]="isDateRangeFilterOpen"
            cdkConnectedOverlayHasBackdrop="true"
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            (backdropClick)="isDateRangeFilterOpen=false"
          >
            <app-date-range-picker
              [startDate]="(filters.startDate ?? firstDprDate) | cloneDeep"
              [endDate]="(filters.endDate ?? lastDprDate) | cloneDeep"
              [minDate]="firstDprDate"
              [maxDate]="lastDprDate"
              (cancel)="isDateRangeFilterOpen=false"
              (dateRangeChanged)="onDateRangeFilterChanged($event)"
            ></app-date-range-picker>
          </ng-template>
        </ng-container>
        <div class="flex-grow-1"></div>

        <!-- Clear filters-->
        <button
          data-testid="clear-all-filters"
          *ngIf="hasFiltersApplied"
          mat-button
          (click)="onClearFilters()"
          color="primary"
          [disabled]="(isAppOnline$ | async) === false"
        >
          <mat-icon svgIcon="filter-remove"></mat-icon>
          <span i18n="@@dpr.dprPage.btnClearFilters">Clear Filters</span>
        </button>
      </div>
    </div>

    <app-dpr-list
      *ngIf="selectedSubproject.isReleased; else subprojectNotReleased"
      [dprCards]="dprCards$.asObservable() | async"
      [canEdit]="canEdit"
      [approvers]="approverList$ | async"
      [onlineApprovers]="onlineApproverList$ | async"
      [accountId]="accountId"
      [clientId]="clientId"
      [clientName]="clientName"
      [isDigitalSignatureEnabled]="isDigitalSignatureEnabled"
      [canEditExternalPartyComment]="canEditExternalPartyComment"
      [customNames]="customNames"
      [isAppOnline]="isAppOnline$ | async"
      [isLoadingInProgress]="isLoadingBusy"
      [isFullScreenMode]="isFullScreenMode$ | async"
      (scrolled)="loadMoreCards()"
    ></app-dpr-list>
    <ng-template #subprojectNotReleased>
      <div class="px-spacer-responsive py-3 border-top">
        <i i18n="@@dpr.dprsPage.txtSubprojectNotReleased">
          Subproject is not released. Go to Project Setup → Release Subproject.
        </i>
      </div>
    </ng-template>
    <div class="d-flex justify-content-center">
      <button
        *ngIf="hasMoreResults && !isLoadingBusy"
        class="mat-secondary tet"
        mat-flat-button
        (click)="loadMoreCards()"
        i18n="@@dpr.dprsPage.btnLoadMore"
      >
        Load More
      </button>
    </div>
    <div *ngIf="!hasMoreResults && hasMoreThanOnePage">
      <p
        data-testid="dprs-end-of-list"
        i18n="@@dpr.dprsPage.txtEndOfList"
        class="font-weight-bold mb-3 px-3 text-center"
      >
        End of list
      </p>
    </div>
  </div>
</ng-container>
